import React from 'react';
import { createRoot } from 'react-dom/client';

import { FAQAccordion } from '@shared/FAQAccordion';

function buildPriceAnswer(city, stats) {
  const intro = "Les tarifs varient d'une auto-école à l'autre, ainsi que le contenu du forfait.";
  if (stats.avg_manual_price && stats.avg_auto_price) {
    return `${intro} Le tarif moyen d'une leçon de conduite en boîte manuelle à ${city} est de <strong>${stats.avg_manual_price}€</strong>. Le tarif moyen d'une leçon sur boîte automatique est de <strong>${stats.avg_auto_price}€</strong>.`;
  }
  if (stats.avg_manual_price) {
    return `${intro} Le tarif moyen d'une leçon de conduite en boîte manuelle à ${city} est de <strong>${stats.avg_manual_price}€</strong>.`;
  }
  if (stats.avg_auto_price) {
    return `${intro} Le tarif moyen d'une leçon de conduite sur boîte automatique à ${city} est de <strong>${stats.avg_auto_price}€</strong>.`;
  }
  return null;
}

function buildFaqList(city, stats) {
  const count = stats.schools_count;
  if (!count) return null;

  const questions = [
    {
      id: 'city-school-1',
      question: `Combien d'auto-écoles y-a-t'il à ${city} ?`,
      answer: `${city} compte actuellement <strong>${count} auto-école${
        count > 1 ? 's' : ''
      }</strong> en activité.`,
    },
  ];

  const priceAnswer = buildPriceAnswer(city, stats);
  if (priceAnswer) {
    questions.push({
      id: 'city-school-2',
      question: `Quel est le prix moyen du permis B à ${city} ?`,
      answer: priceAnswer,
    });
  }

  // Requires: top_success_school: { name, rate, year }, top_candidates_school: { name, count }
  if (stats.top_success_school) {
    const { name: topName, rate, year } = stats.top_success_school;
    let answer = `L'Auto-école <strong>${topName}</strong> a obtenu un taux de réussite de <strong>${rate}%</strong> en ${year}. Il s'agit du taux de réussite le plus élevé à ${city}.`;
    if (stats.top_candidates_school) {
      const { name: candName, count: candCount } = stats.top_candidates_school;
      answer += ` L'Auto-école <strong>${candName}</strong> est celle qui a présenté le plus de candidats (${candCount} élèves).`;
    }
    questions.push({
      id: 'city-school-3',
      question: `Quelle auto-école a le meilleur taux de réussite à ${city} ?`,
      answer,
    });
  }

  // Requires: most_reviewed_school: { name, count }, best_rated_school: { name, rating }
  if (stats.most_reviewed_school || stats.best_rated_school) {
    let answer = `Les auto-écoles de ${city} sont notées par leurs anciens élèves.`;
    if (stats.most_reviewed_school) {
      const { name, count: reviewCount } = stats.most_reviewed_school;
      answer += ` À ${city}, l'auto-école <strong>${name}</strong> est celle qui dispose du plus grand nombre d'avis (${reviewCount} avis).`;
    }
    if (stats.best_rated_school) {
      const { name, rating } = stats.best_rated_school;
      answer += ` L'auto-école <strong>${name}</strong> est celle qui a la meilleure note (${rating}/5).`;
    }
    questions.push({
      id: 'city-school-4',
      question: `Quelle est l'auto-école la mieux notée à ${city} ?`,
      answer,
    });
  }

  // Requires: permis_1_euro_count
  if (stats.permis_1_euro_count != null) {
    questions.push({
      id: 'city-school-5',
      question: `Combien d'auto-écoles proposent le permis à 1€ à ${city} ?`,
      answer: `Le permis à 1€ par jour est une aide financière proposée par <strong>${
        stats.permis_1_euro_count
      } auto-école${stats.permis_1_euro_count > 1 ? 's' : ''}</strong> à ${city}.`,
    });
  }

  // Requires: cpf_count
  if (stats.cpf_count != null) {
    questions.push({
      id: 'city-school-6',
      question: `Combien d'auto-écoles sont éligibles au CPF à ${city} ?`,
      answer: `Il y a actuellement <strong>${stats.cpf_count} auto-école${
        stats.cpf_count > 1 ? 's' : ''
      }</strong> proposant le financement CPF (compte personnel de formation) à ${city}.`,
    });
  }

  return {
    sections: [{ title: null, questions }],
  };
}

document.addEventListener('DOMContentLoaded', () => {
  const node = document.querySelector('#react-faq-accordion');

  if (node) {
    const cityDataAttr = node.dataset.city;
    if (!cityDataAttr) return;

    let faqList;
    try {
      const { city, stats } = JSON.parse(cityDataAttr);
      faqList = buildFaqList(city, stats);
    } catch (_e) {
      return;
    }

    if (!faqList) return;

    const root = createRoot(node);
    root.render(<FAQAccordion faqList={faqList} />);
  }
});
